import React from 'react';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { z } from 'zod';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DatabaseRevision } from 'libs/end-to-end/src/types/databases/revisions/database-revision';
import { TransformDictionaryRow } from 'libs/end-to-end/src/types/databases/transform-dictionary-row';

const DictionaryResponse = z.object({
  rows: z.array(TransformDictionaryRow),
});

export const useDataDictionaryAvailability = (): { isAvailable: boolean } => {
  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();
  const orgId = selectedOrganizationId;
  const databaseId = selectedDatabaseId;
  const revisionId = selectedDatabaseRevisionId;

  const getRevisionUrlFn = useCallback(
    () =>
      orgId && databaseId && revisionId
        ? `${getApiBaseUrl()}/organizations/${orgId}/databases/${databaseId}/revisions/${revisionId}`
        : null,
    [orgId, databaseId, revisionId],
  );
  const revisionReadPattern = useSimpleReadPattern({
    getUrlFn: getRevisionUrlFn,
    canRead: !!orgId && !!databaseId && !!revisionId,
    responseSchema: DatabaseRevision,
  });

  const transformId = useMemo<string | null>(() => {
    if (revisionReadPattern.status !== 'done' || !revisionReadPattern.entity) {
      return null;
    }
    return revisionReadPattern.entity.transformId ?? null;
  }, [revisionReadPattern]);

  const getDictionaryUrlFn = useCallback(
    () =>
      orgId && databaseId && transformId
        ? `${getApiBaseUrl()}/organizations/${orgId}/databases/${databaseId}/transforms/${transformId}/dictionary`
        : null,
    [orgId, databaseId, transformId],
  );
  const dictionaryReadPattern = useSimpleReadPattern({
    getUrlFn: getDictionaryUrlFn,
    canRead: !!orgId && !!databaseId && !!transformId,
    responseSchema: DictionaryResponse,
  });

  const isAvailable = useMemo(
    () =>
      dictionaryReadPattern.status === 'done' &&
      dictionaryReadPattern.entity.rows.length > 0,
    [dictionaryReadPattern],
  );

  return { isAvailable };
};
